import { Element, Node, Text } from "slate";
import { CustomText } from "../slate-types";

/**
 * Helper function to apply inline styles (color and font-size) to a block or inline element.
 * @param node - The Slate node that may have style attributes
 * @returns A string representing inline styles
 */
const getInlineStyles = (node: Element | CustomText): string => {
  let style = "";

  if ((node as Element).align) {
    style += `text-align: ${(node as Element).align};`;
  }
  if (node.color) {
    style += `color: ${node.color};`;
  }
  if (node.background) {
    style += `background: ${node.background};`;
  }
  if (node.fontFamily) {
    style += `font-family: ${node.fontFamily};`;
  }
  if (node.fontSize) {
    const fontSize = /^\d+$/.test(node.fontSize as string) ? node.fontSize + "px" : node.fontSize;
    style += `font-size: ${fontSize};`;
  }
  if ((node as Element).type === "img") {
    style += `max-width: 100%;`;
    if ((node as Element).width) {
      style += `width: ${(node as Element).width};`;
    }
  }

  return style ? ` style="${style}"` : "";
};

const getAttributes = (node: Element): string => {
  let attrs = "";
  if (node.start) {
    attrs += ` start="${node.start}"`;
  }
  if (node.listType) {
    attrs += ` type="${node.listType}"`;
  }
  return attrs;
};

/**
 * Function to serialize Slate nodes to HTML with inline styles for color and font size
 * @param {Node} node - The Slate node to serialize
 * @returns {SerializedHTML} - Serialized HTML string
 */
const serializeNode = (node: Node): string => {
  if (Text.isText(node)) {
    // Handle text-level formatting (inline styles)
    let textContent: string = node.text.replace(/\n/g, "<br>"); // Replace newlines with <br>

    // Handle inline text formatting (bold, italic, underline, etc.)
    if (node.bold) textContent = `<strong>${textContent}</strong>`;
    if (node.italic) textContent = `<i>${textContent}</i>`;
    if (node.underline) textContent = `<u>${textContent}</u>`;
    if (node.strikethrough) textContent = `<s>${textContent}</s>`;
    if (node.code) textContent = `<code>${textContent}</code>`;

    // Apply inline styles (color, fontSize) to inline elements like span
    const inlineStyles = getInlineStyles(node);
    return `<span${inlineStyles}>${textContent}</span>`;
  } else if (Element.isElement(node)) {
    // Handle block elements and apply inline styles at block level
    const blockStyles = getInlineStyles(node);
    const attributes = getAttributes(node);
    switch (node.type) {
      case "paragraph": {
        let children;
        if (node.children.length === 1 && (node.children[0] as Text).text === "") {
          children = "&nbsp;";
        } else {
          children = node.children.map(serializeNode).join("");
        }
        return `<p${blockStyles}${attributes}>${children}</p>`;
      }
      case "h1":
        return `<h1${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h1>`;
      case "h2":
        return `<h2${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h2>`;
      case "h3":
        return `<h3${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h3>`;
      case "h4":
        return `<h4${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h4>`;
      case "h5":
        return `<h5${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h5>`;
      case "h6":
        return `<h6${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</h6>`;
      case "ol":
        return `<ol${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</ol>`;
      case "ul":
        return `<ul${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</ul>`;
      case "li":
        return `<li${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</li>`;
      case "link":
        return `<a href="${node.url}"${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</a>`;
      case "quote":
        return `<blockquote${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</blockquote>`;
      case "code":
        return `<code${blockStyles}${attributes}>${node.children.map(serializeNode).join("")}</code>`;
      case "img": {
        let widthValue = "";
        // Extract just the numeric portion from width value
        if (node.width) {
          const numericWidth = String(node.width).match(/\d+(?:\.\d+)?/);
          if (numericWidth) {
            widthValue = numericWidth[0];
          }
        }
        return `<div><img${blockStyles} width="${widthValue}" src="${node.src}" alt="${node.alt}"/></div>`;
      }
      case "mention":
        return `<span>@${node.id}</span>`;
      case "span":
      default:
        // If the node type is not recognized, fallback to just serializing the children
        return node.children ? `<span>${node.children.map(serializeNode).join("")}</span>` : "<span></span>";
    }
  } else {
    console.warn(`unknown node type: `, node);
    return "";
  }
};

/**
 * Function to serialize a whole Slate document (array of nodes)
 * @param {Node[]} nodes - The Slate document (an array of nodes)
 * @returns {string} - Serialized HTML string for the entire document
 */
export const serialize = (nodes: Node[]): string => {
  return nodes.map((node) => serializeNode(node)).join("");
};
